import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';

const BASE_URL = import.meta.env.VITE_API_URL || 'https://api-streamline.brokoders.com/api';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// Helper to decode JWT and check expiration
const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp - bufferSeconds * 1000;
  } catch {
    return true;
  }
};

// Helper to get token expiration time in ms
const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
};

class ApiClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<string | null> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    this.setupInterceptors();
    this.initializeTokenRefresh();
  }

  private initializeTokenRefresh() {
    // Listen for storage changes to detect token updates
    if (typeof window !== 'undefined') {
      // Schedule refresh when app starts if we have valid tokens
      this.scheduleTokenRefresh();
      
      // Also listen for visibility changes to refresh when tab becomes active
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkAndRefreshToken();
        }
      });
    }
  }

  private scheduleTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const accessToken = useAuthStore.getState().accessToken;
    if (!accessToken) return;

    const expirationTime = getTokenExpirationTime(accessToken);
    if (!expirationTime) return;

    // Refresh 2 minutes before expiration
    const refreshTime = expirationTime - Date.now() - 2 * 60 * 1000;
    
    if (refreshTime <= 0) {
      // Token is already expired or about to expire, refresh now
      this.checkAndRefreshToken();
    } else {
      // Schedule refresh
      this.refreshTimer = setTimeout(() => {
        this.checkAndRefreshToken();
      }, Math.min(refreshTime, 2147483647)); // Max setTimeout value
    }
  }

  private async checkAndRefreshToken() {
    const { accessToken, refreshToken, isAuthenticated } = useAuthStore.getState();
    
    if (!isAuthenticated || !refreshToken) return;
    
    // Check if access token is expired or about to expire (within 2 minutes)
    if (accessToken && isTokenExpired(accessToken, 120)) {
      await this.refreshTokenSilently();
    }
    
    // Schedule next refresh
    this.scheduleTokenRefresh();
  }

  private async refreshTokenSilently(): Promise<string | null> {
    // Prevent multiple refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) {
      return null;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await axios.post<ApiResponse<{ tokens: { accessToken: string; refreshToken: string } }>>(
          `${BASE_URL}/auth/refresh`,
          { refreshToken }
        );

        if (response.data.success && response.data.data?.tokens) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
          useAuthStore.getState().setTokens(accessToken, newRefreshToken);
          this.scheduleTokenRefresh();
          return accessToken;
        }
        return null;
      } catch {
        // If refresh fails, don't logout immediately - let the 401 handler deal with it
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private setupInterceptors() {
    // Request interceptor - add auth token and check if refresh needed
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        let token = useAuthStore.getState().accessToken;
        
        // If token is about to expire, try to refresh it first
        if (token && isTokenExpired(token, 30)) {
          const newToken = await this.refreshTokenSilently();
          if (newToken) {
            token = newToken;
          }
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh on 401
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiResponse>) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 errors - try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshTokenSilently();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
            // No new token - refresh failed, logout and redirect
            this.handleAuthFailure();
            return Promise.reject(error);
          } catch (refreshError) {
            // Refresh failed, logout user
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private handleAuthFailure() {
    useAuthStore.getState().logout();
    // Only redirect if not already on login/register page
    if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
      window.location.href = '/login';
    }
  }

  // Public method to force token refresh
  async forceRefreshToken(): Promise<boolean> {
    const newToken = await this.refreshTokenSilently();
    return !!newToken;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  // Use for LLM endpoints which can take up to 60s
  async postAi<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, { timeout: 60000 });
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  async upload<T>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }

  // Download blob (for PDFs, images, etc.)
  async downloadBlob(url: string, filename: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });
    
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Get blob (for when you need the blob itself)
  async getBlob(url: string): Promise<Blob> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });
    return new Blob([response.data]);
  }
}

export const api = new ApiClient();

// Export typed API response type
export type { ApiResponse };
